import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import { userService } from '../../_services';
import { validatePhoneNumber } from '../../_services/validation.service';
import PagesWrapper from '@/components/ui/shared/PagesWrapper';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { BetaBadge } from '@/components/ui/BetaBadge';
import { Textarea } from '@/components/ui/textarea';
import { Paintbrush, Download, RotateCcw, ImagePlus, X, AlertCircle, FileText, Check, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import behalfIcon1    from '../css/dark/img/whl/behalf-icon1.png';
import behalfIcon2    from '../css/dark/img/whl/behalf-icon2.png';
import behalfIcon3    from '../css/dark/img/whl/behalf-icon3.png';
import introBg        from '../css/dark/img/whl/intro.png';
import pharmaLogo1    from '../css/dark/img/whl/pharmacist_logo1.jpg';
import pharmaLogo2    from '../css/dark/img/whl/pharmacist_logo2.jpg';
import pharmaLogo3    from '../css/dark/img/whl/pharmacist_logo3.jpg';
import pharmaLogo4    from '../css/dark/img/whl/pharmacist_logo4.jpg';
import pharmaLogo5    from '../css/dark/img/whl/pharmacist_logo5.jpg';
import pharmaLogo6    from '../css/dark/img/whl/pharmacist_logo6.jpg';
import pharmaLogo7    from '../css/dark/img/whl/pharmacist_logo7.jpg';
import pharmaLogo8    from '../css/dark/img/whl/pharmacist_logo8.jpg';
import pharmaLogo9    from '../css/dark/img/whl/pharmacist_logo9.jpg';
import iconFacebook    from '../css/dark/img/whl/facebook.svg';
import iconLinkedin    from '../css/dark/img/whl/linked-in.svg';
import iconTwitter     from '../css/dark/img/twitter.png';
import iconInstagram   from '../css/dark/img/instagram.png';
import imgAppleWallet  from '../css/dark/img/whl/apple_wallet.png';
import imgGoogleWallet from '../css/dark/img/whl/google_wallet.png';
import iconSearch      from '../css/dark/img/whl/search.svg';

const PHARMA_LOGO_SRCS  = [
    pharmaLogo1, pharmaLogo2, pharmaLogo3, pharmaLogo4, pharmaLogo5,
    pharmaLogo6, pharmaLogo7, pharmaLogo8, pharmaLogo9,
];

const SOCIAL_ICON_FIELDS = ['socialFacebook', 'socialInstagram', 'socialTwitter', 'socialLinkedin'];

const SOCIAL_ICON_BY_FIELD = {
    socialFacebook : iconFacebook,
    socialInstagram: iconInstagram,
    socialTwitter  : iconTwitter,
    socialLinkedin : iconLinkedin,
};

const SOCIAL_LABELS = {
    socialFacebook : 'Facebook',
    socialInstagram: 'Instagram',
    socialTwitter  : 'Twitter',
    socialLinkedin : 'LinkedIn',
};
const WALLET_SRCS        = [imgAppleWallet, imgGoogleWallet];

const LOGO_MIN_PX = 200;
const LOGO_MAX_PX = 3000;
const LOGO_ACCEPT = 'image/png,image/svg+xml,.png,.svg';
const LOGO_MIME_TYPES = ['image/png', 'image/svg+xml'];
const LOGO_EXT_PATTERN = /\.(png|svg)$/i;
const PHONE_MAX_LENGTH = 20;

const DOMAIN_REGEX = /^(?:https?:\/\/)?(?:([a-zA-Z0-9-]+)\.)?([a-zA-Z0-9-]+)\.([a-zA-Z]{2,})(?:\/|$)/;

const brandDisplayName = (design) => design.programName?.trim() || 'DemoRx';
const legalDisplayName = (design) => design.legalName?.trim() || design.programName?.trim() || 'DemoRx';

const WHITELABEL_DNS_RECORDS = {
    cname: { type: 'CNAME', host: 'www', value: 'whitelabel.glichealth.com' },
    aRecord: { type: 'A Record', host: '@', value: '20.81.42.156' },
};

// Full-page canvas dimensions (desktop webpage preview)
// NOTE: FOOT_H and DISC_H are computed dynamically inside drawCanvas;
//       CANVAS_H is intentionally removed — canvas.height is set dynamically.
const CANVAS_W  = 1080;
const NAV_H     = 64;
const HERO_H    = 480;
const LOGOS_H   = 128;
const HOW_H     = 180;
const CARD_PAD  = 56;
const CARD_COL_GAP   = 32;
const CARD_TOP_PAD_C = 56;
const CARD_BTN_AREA_C = 24 + 44 + 24;

// Footer / disclaimer vertical rhythm (links < disclaimer body < copyright bar)
const FOOT_PAD_V      = 40;
const DISC_PAD_V      = 48;
const DISC_INNER_GAP  = 16;
const COPY_PAD_V      = 24;
const TM_LINE_H       = 14;
const DISC_LINE_H     = 15;
const COPY_TEXT_BASE  = 14;
const FOOT_BRAND_LINE = 20;
const FOOT_LINK_GAP   = 28;
const FOOT_LINK_STEP  = 22;
const FOOT_COL2_STEP  = 24;

const FOOT_LEFT_LINKS  = ['Home', 'Contact Us', 'My Cards'];
const FOOT_RIGHT_LINKS = ['Download Mobile App', 'Help', 'Terms of Use', 'Drugs Covered', 'Consumer CCPA Privacy Notice at Collection'];

function hasFooterSocial(design) {
    return !!(design.socialFacebook || design.socialInstagram || design.socialTwitter || design.socialLinkedin);
}

function getFooterLinksContentHeight(design) {
    const col1H = FOOT_BRAND_LINE + FOOT_LINK_GAP + (FOOT_LEFT_LINKS.length - 1) * FOOT_LINK_STEP + 14;
    const col2H = FOOT_BRAND_LINE + (FOOT_RIGHT_LINKS.length - 1) * FOOT_COL2_STEP + 14;
    const col3H = hasFooterSocial(design)
        ? 20 + 20 + 24 + 16 + FOOT_BRAND_LINE + 16 + 46
        : FOOT_BRAND_LINE + 16 + 46;
    return Math.max(col1H, col2H, col3H);
}

function measureDisclaimerHeight(ctx, design) {
    const DISC_PAD_H = 48;
    const DISC_MAX_W = CANVAS_W - DISC_PAD_H * 2;
    const progName = legalDisplayName(design);
    ctx.font = '14px Inter, system-ui, sans-serif';
    const tmLines = wrapText(ctx, 'Pharmacy names, logos, brands, and other trademarks are the property of their respective owners.', DISC_MAX_W);
    const discSegs = [
        { bold: true,  text: `The ${progName} prescription discount program is NOT insurance` },
        { bold: false, text: `. It is also not a Medicare prescription drug plan. The range of prescription discounts provided under this discount program will vary depending on the prescription and pharmacy where the prescription is purchased and can be up to $95 off the retail price. ` },
        { bold: true,  text: `Purchases using the ${progName} program do not count towards insurance or Medicare deductibles or out-of-pocket maximums.` },
        { bold: false, text: ` You are fully responsible for paying your prescriptions at the pharmacy at the time of service, but you will be entitled to receive a discount from the pharmacy in accordance with the specific pre-negotiated discounted rate schedule. Glic Health LLC is the Administrator of the ${progName} prescription discount program with its office located at One Marina Park Drive, Suite 1410, Boston MA 02210. Toll Free at (844) 944-4542 and email at support@glichealth.com. For additional information, including an up-to-date list of pharmacies and covered drugs, or assistance with any problems related to this prescription drug discount plan, please contact our member support line toll free at (800) 999-3053 or visit ${progName}.com. By using the ${progName} prescription discount program or app, you agree to the ${progName} Terms and Conditions found at https://demo.glicrx.com/terms.` },
    ];
    const discBodyY = DISC_PAD_V + tmLines.length * TM_LINE_H + DISC_INNER_GAP;
    const nDiscLines = drawRichText(ctx, discSegs, DISC_PAD_H, discBodyY, DISC_MAX_W, DISC_LINE_H, 9.5, '', true);
    const copyDividerY = discBodyY + nDiscLines * DISC_LINE_H + DISC_PAD_V;
    return copyDividerY + COPY_PAD_V * 2 + COPY_TEXT_BASE;
}

const DEFAULT_DESIGN = {
    // Business info
    legalName      : '',
    mailingAddress : '',
    contactEmail   : '',
    contactPhone   : '',
    // Brand
    programName    : '',
    tagline        : '',
    // Hero
    headline       : 'Save up to $95 on your prescriptions',
    ctaText        : 'Find the Lowest Price',
    // Card fields
    bin            : '023518',
    pcn            : 'ARX',
    group          : '',
    memberId       : '100-4542',
    website        : '',
    phone          : '',
    // Colors
    navColor       : '#1a3a5c',
    brandColor     : '#2563eb',
    linkColor      : '#2563eb',
    headingColor   : '#1d252d',
    paragraphColor : '#506d85',
    btnBgColor     : '#2563eb',
    btnFgColor     : '#ffffff',
    // Media
    logoDataUrl    : null,
    pdfFileName    : null,
    pdfFileUrl     : null,
    // Social media
    socialFacebook  : '',
    socialInstagram : '',
    socialTwitter   : '',
    socialLinkedin  : '',
    // Card images
    cardFrontDataUrl: null,
    cardBackDataUrl : null,
};

// ── Canvas helpers ──────────────────────────────────────────────────────────

function wrapText(ctx, text, maxWidth) {
    const words = text.split(' ');
    const lines = [];
    let current = '';
    for (const word of words) {
        const test = current ? `${current} ${word}` : word;
        if (ctx.measureText(test).width <= maxWidth) {
            current = test;
        } else {
            if (current) lines.push(current);
            current = word;
        }
    }
    if (current) lines.push(current);
    return lines;
}

// Renders inline mixed bold/normal text with automatic word-wrap.
// segments: [{text: string, bold: boolean}]
// Returns number of lines rendered. Pass dryRun=true to measure without drawing.
function drawRichText(ctx, segments, x, startY, maxW, lineH, fontSize, color, dryRun = false) {
    const nFont = `${fontSize}px Inter, system-ui, sans-serif`;
    const bFont = `bold ${fontSize}px Inter, system-ui, sans-serif`;

    // Flatten segments into [{w, bold}] tokens
    const tokens = [];
    for (const seg of segments) {
        seg.text.split(/\s+/).filter(Boolean).forEach(w => tokens.push({ w, bold: seg.bold }));
    }

    // Build lines
    const lines = [];
    let curLine = [], curW = 0;
    for (const tok of tokens) {
        ctx.font = tok.bold ? bFont : nFont;
        const ww = ctx.measureText(tok.w).width;
        // No space before punctuation-only tokens
        const noSpace = !curLine.length || /^[.,;:!?)\]'"]/.test(tok.w);
        ctx.font = nFont;
        const sw = noSpace ? 0 : ctx.measureText(' ').width;
        if (curLine.length && curW + sw + ww > maxW) {
            lines.push(curLine);
            curLine = [tok];
            curW    = ww;
        } else {
            curW += sw + ww;
            curLine.push(tok);
        }
    }
    if (curLine.length) lines.push(curLine);

    // Render
    if (!dryRun) {
        ctx.fillStyle = color;
        lines.forEach((ln, li) => {
            let cx = x;
            ln.forEach((tok, ti) => {
                const noSpace = ti === 0 || /^[.,;:!?)\]'"]/.test(tok.w);
                if (!noSpace) {
                    ctx.font = nFont;
                    cx += ctx.measureText(' ').width;
                }
                ctx.font = tok.bold ? bFont : nFont;
                ctx.fillText(tok.w, cx, startY + li * lineH);
                cx += ctx.measureText(tok.w).width;
            });
        });
    }

    return lines.length;
}

function roundedRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
}

function hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
        ? { r: parseInt(result[1], 16), g: parseInt(result[2], 16), b: parseInt(result[3], 16) }
        : { r: 0, g: 0, b: 0 };
}

function withAlpha(hex, alpha) {
    const { r, g, b } = hexToRgb(hex);
    return `rgba(${r},${g},${b},${alpha})`;
}


function truncateText(ctx, text, maxWidth) {
    const str = String(text ?? '');
    if (ctx.measureText(str).width <= maxWidth) return str;
    let trimmed = str;
    while (trimmed.length > 0 && ctx.measureText(`${trimmed}…`).width > maxWidth) {
        trimmed = trimmed.slice(0, -1);
    }
    return `${trimmed}…`;
}

const MINI_CARD_W = 360;
const MINI_CARD_H = 200;
const CARD_ASPECT_RATIO = MINI_CARD_W / MINI_CARD_H;

const isUploadedCardImage = (img) => !!(img?.complete && img.naturalWidth > 0);

function getFixedCardSlotHeight(slotW) {
    return slotW * (MINI_CARD_H / MINI_CARD_W);
}

/** Fill slot width; scale to cover box height; align top — excess clipped at bottom */
function drawImageCoverTop(ctx, img, x, y, w, h) {
    if (!img?.complete || !img.naturalWidth) return;
    const scale = Math.max(w / img.naturalWidth, h / img.naturalHeight);
    const dw = img.naturalWidth * scale;
    const dh = img.naturalHeight * scale;
    const dx = x + (w - dw) / 2;
    const dy = y;
    ctx.drawImage(img, dx, dy, dw, dh);
}

// ── Mini pharmacy card drawn inside the full-page canvas ────────────────────

function drawMiniCard(ctx, x, y, w, h, design, logoImg, embedded = false) {
    const R = 10, HDR = 30;

    if (!embedded) {
        // Drop shadow
        ctx.save();
        ctx.shadowColor   = 'rgba(0,0,0,0.14)';
        ctx.shadowBlur    = 18;
        ctx.shadowOffsetY = 6;
        roundedRect(ctx, x, y, w, h, R);
        ctx.fillStyle = '#ffffff';
        ctx.fill();
        ctx.restore();

        // Border
        ctx.save();
        roundedRect(ctx, x, y, w, h, R);
        ctx.strokeStyle = '#e2e8f0';
        ctx.lineWidth   = 1;
        ctx.stroke();
        ctx.restore();
    }

    // Header bar
    ctx.save();
    roundedRect(ctx, x, y, w, h, R);
    ctx.clip();
    ctx.fillStyle = design.navColor;
    ctx.fillRect(x, y, w, HDR);
    ctx.restore();

    ctx.save();
    ctx.textBaseline = 'middle';
    ctx.font         = '500 16px Inter, system-ui, sans-serif';
    ctx.fillStyle    = '#ffffff';
    ctx.fillText('Pharmacy Discount Card', x + 12, y + HDR / 2);
    ctx.restore();

    // Logo
    const ML = 12, MY = HDR + 10, MS = 32;
    const nameMaxW = w - ML - MS - 8 - 100;
    const colHalfW = w / 2 - 24;
    if (logoImg) {
        ctx.save();
        roundedRect(ctx, x + ML, y + MY, MS, MS, 4);
        ctx.clip();
        ctx.drawImage(logoImg, x + ML, y + MY, MS, MS);
        ctx.restore();
    } else {
        roundedRect(ctx, x + ML, y + MY, MS, MS, 4);
        ctx.fillStyle    = '#f1f5f9';
        ctx.fill();
        ctx.strokeStyle  = '#e2e8f0';
        ctx.lineWidth    = 1;
        ctx.stroke();
    }

    // Program name + "Member Support"
    ctx.font      = 'bold 14px Inter, system-ui, sans-serif';
    ctx.fillStyle = '#1a202c';
    ctx.fillText(truncateText(ctx, brandDisplayName(design), nameMaxW), x + ML + MS + 8, y + MY + 14);
    ctx.font      = '14px Inter, system-ui, sans-serif';
    ctx.fillStyle = '#64748b';
    ctx.textAlign = 'right';
    ctx.fillText('Member Support', x + w - 12, y + MY + 14);
    ctx.textAlign = 'left';

    // Divider
    const div1Y = y + MY + MS + 8;
    ctx.strokeStyle = '#e2e8f0';
    ctx.lineWidth   = 0.5;
    ctx.beginPath();
    ctx.moveTo(x + 12, div1Y);
    ctx.lineTo(x + w - 12, div1Y);
    ctx.stroke();

    // Fields (BIN, PCN, Group, Member ID)
    const col2X = x + w / 2 - 10;
    const fields = [
        { label: 'BIN',                    val: design.bin      || '023518',   fx: x + 14, fy: div1Y + 18 },
        { label: 'PCN',                    val: design.pcn      || 'ARX',      fx: col2X,  fy: div1Y + 18 },
        { label: 'Group # (Access Code)',  val: design.group    || 'GHRX525',  fx: x + 14, fy: div1Y + 62 },
        { label: 'MEMBER ID',              val: design.memberId || '100-4542', fx: col2X,  fy: div1Y + 62 },
    ];
    fields.forEach(f => {
        ctx.font      = '8px Inter, system-ui, sans-serif';
        ctx.fillStyle = '#94a3b8';
        ctx.fillText(f.label, f.fx, f.fy);
        ctx.font      = 'bold 14px Inter, system-ui, sans-serif';
        ctx.fillStyle = '#1a202c';
        ctx.fillText(truncateText(ctx, f.val, colHalfW), f.fx, f.fy + 18);
    });

    // Disclaimer
    ctx.font      = 'italic 8px Inter, system-ui, sans-serif';
    ctx.fillStyle = '#94a3b8';
    ctx.fillText('This is not insurance. This card is free.', x + 14, y + h - 10);
}

// ── Main draw function ──────────────────────────────────────────────────────

function drawCanvas(canvas, design, logoImg, stepImgs = [], introBgImg = null, pharmaImgs = [], socialIcons = {}, walletImgs = [], searchIcon = null, cardFrontImg = null, cardBackImg = null) {
    const ctx = canvas.getContext('2d');

    const actualFootH = FOOT_PAD_V * 2 + getFooterLinksContentHeight(design);
    const actualDiscH = measureDisclaimerHeight(ctx, design);

    const colW       = (CANVAS_W - CARD_PAD * 2 - CARD_COL_GAP) / 2;
    const showCardSection = isUploadedCardImage(cardFrontImg) && isUploadedCardImage(cardBackImg);
    const cardSlotH  = showCardSection ? getFixedCardSlotHeight(colW) : 0;
    const actualCardH = showCardSection ? CARD_TOP_PAD_C + cardSlotH + CARD_BTN_AREA_C : 0;

    const BASE_H = NAV_H + HERO_H + LOGOS_H + actualCardH + HOW_H + actualFootH;
    canvas.height = BASE_H + actualDiscH;

    ctx.clearRect(0, 0, CANVAS_W, canvas.height);

    // ── 1. NAVBAR (white, matches screenshot) ────────────────────────────────
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, CANVAS_W, NAV_H);
    // Bottom border
    ctx.strokeStyle = '#e2e8f0';
    ctx.lineWidth   = 1;
    ctx.beginPath();
    ctx.moveTo(0, NAV_H);
    ctx.lineTo(CANVAS_W, NAV_H);
    ctx.stroke();

    const navLogoSz = 36, navLogoX = 32, navLogoY = (NAV_H - navLogoSz) / 2;
    if (logoImg) {
        const aspect = logoImg.naturalWidth / logoImg.naturalHeight;
        const drawH  = navLogoSz;
        const drawW  = Math.min(drawH * aspect, 160);
        ctx.save();
        roundedRect(ctx, navLogoX, navLogoY, drawW, drawH, 4);
        ctx.clip();
        ctx.drawImage(logoImg, navLogoX, navLogoY, drawW, drawH);
        ctx.restore();
    }

    ctx.save();
    ctx.textBaseline = 'middle';
    ctx.font         = 'bold 18px Inter, system-ui, sans-serif';
    ctx.fillStyle    = '#1e293b';
    ctx.fillText(
        logoImg ? '' : brandDisplayName(design),
        navLogoX,
        NAV_H / 2
    );

    const navLinks = ['Home', 'Contact Us', 'Download Mobile App', 'FAQ'];
    ctx.font      = '14px Inter, system-ui, sans-serif';
    ctx.fillStyle = '#475569';
    let linkCurX  = CANVAS_W - 32;
    [...navLinks].reverse().forEach((link, ri) => {
        const lw = ctx.measureText(link).width;
        linkCurX -= lw;
        ctx.fillText(link, linkCurX, NAV_H / 2);
        // Underline "Home" (last item in reversed loop = index 0 forward)
        if (ri === navLinks.length - 1) {
            ctx.strokeStyle = design.linkColor;
            ctx.lineWidth   = 2;
            ctx.beginPath();
            ctx.moveTo(linkCurX, NAV_H - 6);
            ctx.lineTo(linkCurX + lw, NAV_H - 6);
            ctx.stroke();
            ctx.fillStyle = design.linkColor;
            ctx.fillText(link, linkCurX, NAV_H / 2);
        }
        linkCurX -= 28;
    });
    ctx.restore();

    // ── 2. HERO ──────────────────────────────────────────────────────────────
    const HERO_Y = NAV_H;
    if (introBgImg && introBgImg.complete && introBgImg.naturalWidth > 0) {
        // Clip to hero bounds so image never bleeds into the navbar above
        ctx.save();
        ctx.beginPath();
        ctx.rect(0, HERO_Y, CANVAS_W, HERO_H);
        ctx.clip();

        // Cover-fit: scale so image fills the entire hero rectangle
        const iw = introBgImg.naturalWidth, ih = introBgImg.naturalHeight;
        const scale = Math.max(CANVAS_W / iw, HERO_H / ih);
        const dw = iw * scale, dh = ih * scale;
        const dx = (CANVAS_W - dw) / 2, dy = HERO_Y + (HERO_H - dh) / 2;
        ctx.drawImage(introBgImg, dx, dy, dw, dh);
        ctx.restore();

        // Subtle overlay so text stays readable
        ctx.fillStyle = 'rgba(255,255,255,0.55)';
        ctx.fillRect(0, HERO_Y, CANVAS_W, HERO_H);
    } else {
        const heroGrad = ctx.createLinearGradient(0, HERO_Y, 0, HERO_Y + HERO_H);
        heroGrad.addColorStop(0, '#dbeafe');
        heroGrad.addColorStop(1, '#eff6ff');
        ctx.fillStyle = heroGrad;
        ctx.fillRect(0, HERO_Y, CANVAS_W, HERO_H);
    }

    ctx.font      = 'bold 38px Inter, system-ui, sans-serif';
    ctx.fillStyle = design.headingColor;
    ctx.textAlign = 'center';
    ctx.fillText(design.headline || 'Save up to $95 on your prescriptions', CANVAS_W / 2, HERO_Y + 136);

    // ── Search bar: single container, icon left, button overlaid right ──────
    const srchTotalW = 720, srchH = 52, srchY = HERO_Y + 166;
    const srchX      = (CANVAS_W - srchTotalW) / 2;

    // Container shadow + fill
    ctx.save();
    ctx.shadowColor   = 'rgba(0,0,0,0.14)';
    ctx.shadowBlur    = 20;
    ctx.shadowOffsetY = 4;
    roundedRect(ctx, srchX, srchY, srchTotalW, srchH, 10);
    ctx.fillStyle = '#ffffff';
    ctx.fill();
    ctx.restore();

    // Container border
    roundedRect(ctx, srchX, srchY, srchTotalW, srchH, 10);
    ctx.strokeStyle = '#e2e8f0';
    ctx.lineWidth   = 1.5;
    ctx.stroke();

    // Search icon (lens) on the left
    const icoSz = 20, icoPad = 16;
    const icoY  = srchY + (srchH - icoSz) / 2;
    if (searchIcon && searchIcon.complete && searchIcon.naturalWidth > 0) {
        ctx.drawImage(searchIcon, srchX + icoPad, icoY, icoSz, icoSz);
    } else {
        // Fallback: draw a simple magnifier shape
        ctx.save();
        ctx.strokeStyle = '#94a3b8';
        ctx.lineWidth   = 2;
        ctx.beginPath();
        ctx.arc(srchX + icoPad + 7, srchY + srchH / 2 - 1, 6, 0, Math.PI * 2);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(srchX + icoPad + 12, srchY + srchH / 2 + 4);
        ctx.lineTo(srchX + icoPad + 17, srchY + srchH / 2 + 9);
        ctx.stroke();
        ctx.restore();
    }

    // Placeholder text
    ctx.font      = '14px Inter, system-ui, sans-serif';
    ctx.fillStyle = '#94a3b8';
    ctx.textAlign = 'left';
    ctx.fillText(
        'Type your drug name (e.g pantoprazole, Amlodipine etc.)',
        srchX + icoPad + icoSz + 10, srchY + srchH / 2 + 4
    );

    // CTA button — overlaid inside the right side of the container
    const btnW = 180, btnH = srchH - 8, btnR = 7;
    const btnX = srchX + srchTotalW - btnW - 4;
    const btnY = srchY + 4;
    roundedRect(ctx, btnX, btnY, btnW, btnH, btnR);
    ctx.fillStyle = design.btnBgColor;
    ctx.fill();
    ctx.font      = 'bold 14px Inter, system-ui, sans-serif';
    ctx.fillStyle = design.btnFgColor;
    ctx.textAlign = 'center';
    ctx.fillText(design.ctaText || 'Find the Lowest Price', btnX + btnW / 2, btnY + btnH / 2 + 5);

    // 3-step icons — centered below the search input, equally spaced within its width
    const stepTexts  = [
        'Search for the best price',
        'Get your card',
        'Show pharmacist at time of purchase',
    ];
    const stepIconSz = 56;
    const stepBaseY  = srchY + srchH + 28;   // just below the search bar
    const stepColW   = srchTotalW / 3;       // each column ≈ 240 px
    const stepMaxTW  = stepColW - 24;        // 12 px padding on each side

    ctx.font      = '600 22px Inter, system-ui, sans-serif';
    ctx.fillStyle = design.paragraphColor;
    ctx.textAlign = 'center';

    stepTexts.forEach((text, i) => {
        const sx  = srchX + stepColW * i + stepColW / 2;
        const img = stepImgs[i];

        // Icon
        if (img && img.complete && img.naturalWidth > 0) {
            ctx.drawImage(img, sx - stepIconSz / 2, stepBaseY, stepIconSz, stepIconSz);
        } else {
            ctx.beginPath();
            ctx.arc(sx, stepBaseY + stepIconSz / 2, stepIconSz / 2, 0, Math.PI * 2);
            ctx.fillStyle = withAlpha(design.linkColor, 0.15);
            ctx.fill();
            ctx.fillStyle = design.paragraphColor; // restore for text
        }

        // Word-wrap label within the column width, then draw each line
        const lines = wrapText(ctx, text, stepMaxTW);
        lines.forEach((line, li) => {
            ctx.fillText(line, sx, stepBaseY + stepIconSz + 28 + li * 32);
        });
    });
    ctx.textAlign = 'left';

    // ── 3. PHARMACY LOGOS BAR ────────────────────────────────────────────────
    const LOGOS_Y = HERO_Y + HERO_H;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, LOGOS_Y, CANVAS_W, LOGOS_H);

    ctx.font      = 'bold 16px Inter, system-ui, sans-serif';
    ctx.fillStyle = design.headingColor;
    ctx.textAlign = 'center';
    ctx.marginTop = '50px';
    ctx.fillText('Accepted at over 35,000 Pharmacies nationwide', CANVAS_W / 2, LOGOS_Y + 24);
    ctx.font      = 'bold 14px Inter, system-ui, sans-serif';
    ctx.fillStyle = design.headingColor;
    ctx.marginBottom = '10px';
    ctx.fillText(
        `The ${brandDisplayName(design)} Card is free. This card is not insurance.`,
        CANVAS_W / 2, LOGOS_Y + 42
    );

    // Pharmacy logos — contain-fit each image in a fixed slot
    const phCount  = 9;
    const phSlotW  = 68, phSlotH = 40;
    const phPad    = 48;
    const phGap    = (CANVAS_W - phPad * 2 - phSlotW) / (phCount - 1);
    const phY      = LOGOS_Y + 52;

    for (let i = 0; i < phCount; i++) {
        const slotX = phPad + i * phGap;
        const img   = pharmaImgs[i];
        if (img && img.complete && img.naturalWidth > 0) {
            // Contain-fit into slot
            const scale = Math.min(phSlotW / img.width, phSlotH / img.height);
            const dw    = img.width  * scale;
            const dh    = img.height * scale;
            const dx    = slotX + (phSlotW - dw) / 2;
            const dy    = phY   + (phSlotH - dh) / 2;
            ctx.drawImage(img, dx, dy, dw, dh);
        } else {
            // Fallback placeholder
            ctx.strokeStyle = '#e2e8f0';
            ctx.lineWidth   = 1;
            ctx.strokeRect(slotX, phY, phSlotW, phSlotH);
        }
    }
    ctx.textAlign = 'left';

    // ── 4. CARD + PRINT (only when front and back images are uploaded) ───────
    const CARD_SECT_Y = LOGOS_Y + LOGOS_H;

    if (showCardSection) {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, CARD_SECT_Y, CANVAS_W, actualCardH);

        const MCX = CARD_PAD;
        const MCY = CARD_SECT_Y + CARD_TOP_PAD_C;
        const MCW = colW;
        const CARD_SLOT_RADIUS = 10;

        const drawCardSlot = (img, x, y, w, slotH) => {
            ctx.save();
            ctx.shadowColor   = '#cccccc';
            ctx.shadowBlur    = 15;
            ctx.shadowOffsetX = 0;
            ctx.shadowOffsetY = 0;
            roundedRect(ctx, x, y, w, slotH, CARD_SLOT_RADIUS);
            ctx.fillStyle = '#ffffff';
            ctx.fill();
            ctx.restore();

            ctx.save();
            roundedRect(ctx, x, y, w, slotH, CARD_SLOT_RADIUS);
            ctx.clip();
            ctx.fillStyle = '#f8fafc';
            ctx.fillRect(x, y, w, slotH);
            drawImageCoverTop(ctx, img, x, y, w, slotH);
            ctx.restore();
        };

        drawCardSlot(cardFrontImg, MCX, MCY, MCW, cardSlotH);

        const infoX = CARD_PAD + colW + CARD_COL_GAP;
        const infoY = MCY;
        drawCardSlot(cardBackImg, infoX, infoY, colW, cardSlotH);

        const pBW = 180, pBH = 44;
        const pBX = (CANVAS_W - pBW) / 2;
        const pBY = MCY + cardSlotH + 24;

        ctx.save();
        ctx.shadowColor   = 'rgba(0,0,0,0.20)';
        ctx.shadowBlur    = 10;
        ctx.shadowOffsetY = 3;
        roundedRect(ctx, pBX, pBY, pBW, pBH, 8);
        ctx.fillStyle = design.btnBgColor;
        ctx.fill();
        ctx.restore();

        ctx.font      = 'bold 14px Inter, system-ui, sans-serif';
        ctx.fillStyle = design.btnFgColor;
        ctx.textAlign = 'center';
        ctx.fillText('Print My Card', pBX + pBW / 2, pBY + 27);
        ctx.textAlign = 'left';
    }

    // ── 5. HOW IT WORKS ──────────────────────────────────────────────────────
    const HOW_Y = CARD_SECT_Y + actualCardH;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, HOW_Y, CANVAS_W, HOW_H);

    ctx.font      = 'bold 32px Inter, system-ui, sans-serif';
    ctx.fillStyle = design.headingColor;
    ctx.textAlign = 'center';
    ctx.fillText('How does it work?', CANVAS_W / 2, HOW_Y + 52);

    ctx.font      = '16px Inter, system-ui, sans-serif';
    ctx.fillStyle = design.paragraphColor;
    const brandName = brandDisplayName(design);
    const howLines = wrapText(ctx, `On behalf of consumers nationwide, ${brandName} has negotiated a discount rate with a national network of drugstores and retail outlets. Over 35,000 stores have partnered with ${brandName} to offer prescription assistance to the uninsured and under-insured.`, CANVAS_W / 1.28);
    howLines.forEach((line, i) => ctx.fillText(line, CANVAS_W / 2, HOW_Y + 88 + i * 22));
    /*ctx.fillText(
        'and retail outlets. Over 35,000 stores have partnered to offer prescription assistance to the uninsured and under-insured.',
        CANVAS_W / 2, HOW_Y + 108
    );*/
    //howLines.forEach((line, i) => ctx.fillText(line, CANVAS_W / 2, HOW_Y + 88 + i * 22));
    ctx.textAlign = 'left';

    // ── 6. FOOTER (full-width 3-column) ──────────────────────────────────────
    const FOOT_Y = HOW_Y + HOW_H;
    const footContentTop = FOOT_Y + FOOT_PAD_V;
    ctx.fillStyle = '#f8fafc';
    ctx.fillRect(0, FOOT_Y, CANVAS_W, actualFootH);

    ctx.strokeStyle = '#e2e8f0';
    ctx.lineWidth   = 1;
    ctx.beginPath();
    ctx.moveTo(0, FOOT_Y);
    ctx.lineTo(CANVAS_W, FOOT_Y);
    ctx.stroke();

    // ── Col 1 (x=48): brand name + primary nav links ──
    ctx.font      = 'bold 15px Inter, system-ui, sans-serif';
    ctx.fillStyle = design.headingColor;
    ctx.fillText(brandDisplayName(design), 48, footContentTop + FOOT_BRAND_LINE);

    ctx.font      = '14px Inter, system-ui, sans-serif';
    ctx.fillStyle = design.paragraphColor;
    FOOT_LEFT_LINKS.forEach((link, i) => ctx.fillText(link, 48, footContentTop + FOOT_BRAND_LINE + FOOT_LINK_GAP + i * FOOT_LINK_STEP));

    // ── Col 2 (x=310): secondary nav links (no heading) ──
    const FOOT_COL2_X = 310;
    ctx.font      = '14px Inter, system-ui, sans-serif';
    ctx.fillStyle = design.paragraphColor;
    FOOT_RIGHT_LINKS.forEach((link, i) => ctx.fillText(link, FOOT_COL2_X, footContentTop + FOOT_BRAND_LINE + i * FOOT_COL2_STEP));

    // ── Col 3 (x=640): Follow + social icons inline, then wallet buttons ──
    const FOOT_COL3_X = 640;
    const FOOT_COL3_W = CANVAS_W - 48 - FOOT_COL3_X;  // ≈ 392 px

    const hasSocial = hasFooterSocial(design);
    let walHeadY = footContentTop + FOOT_BRAND_LINE;

    if (hasSocial) {
        // "Follow [brand]" heading
        ctx.font      = 'bold 14px Inter, system-ui, sans-serif';
        ctx.fillStyle = design.headingColor;
        ctx.fillText(`Follow ${brandDisplayName(design)}`, FOOT_COL3_X, footContentTop + FOOT_BRAND_LINE);

        // Only draw icons for links that are actually filled in
        const socialMap = SOCIAL_ICON_FIELDS
            .filter(field => design[field])
            .map(field => ({
                field,
                label: SOCIAL_LABELS[field],
                img: socialIcons[field],
            }));

        const socialIconSz = 20;
        const socialItemW  = Math.min(FOOT_COL3_W / socialMap.length, 110);
        const socialRowY   = footContentTop + FOOT_BRAND_LINE + 20;

        socialMap.forEach((item, i) => {
            const ix  = FOOT_COL3_X + i * socialItemW;
            const iy  = socialRowY;
            if (item.img && item.img.complete && item.img.naturalWidth > 0) {
                ctx.drawImage(item.img, ix, iy, socialIconSz, socialIconSz);
            } else {
                ctx.fillStyle = design.brandColor;
                ctx.fillRect(ix, iy, socialIconSz, socialIconSz);
            }
            ctx.font      = '13px Inter, system-ui, sans-serif';
            ctx.fillStyle = design.paragraphColor;
            ctx.textAlign = 'left';
            ctx.fillText(item.label, ix + socialIconSz + 5, iy + socialIconSz / 2 + 4);
        });

        walHeadY = socialRowY + socialIconSz + 24;
    }

    // "Get Your Discount Card" heading
    ctx.font      = 'bold 14px Inter, system-ui, sans-serif';
    ctx.fillStyle = design.headingColor;
    ctx.fillText('Get Your Discount Card', FOOT_COL3_X, walHeadY);

    // Two wallet buttons side-by-side — black bg, logo left, text right
    const walBtnH  = 46, walBtnR = 6;
    const walBtnW  = (FOOT_COL3_W - 10) / 2;  // ≈ 191 px each
    const walBtnY  = walHeadY + 16;
    const walBtns  = [
        { img: walletImgs[0], label: 'Add to Apple wallet'  },
        { img: walletImgs[1], label: 'Add to Google wallet' },
    ];

    walBtns.forEach((btn, i) => {
        const bx = FOOT_COL3_X + i * (walBtnW + 10);
        const by = walBtnY;

        // Black background
        roundedRect(ctx, bx, by, walBtnW, walBtnH, walBtnR);
        ctx.fillStyle = '#0f172a';
        ctx.fill();

        if (btn.img && btn.img.complete && btn.img.naturalWidth > 0) {
            // Scale logo to fit left portion of button
            const logoH  = walBtnH - 12;
            const scale  = logoH / btn.img.naturalHeight;
            const dw     = btn.img.naturalWidth * scale;
            const dh     = logoH;
            const imgX   = bx + 10;
            const imgY   = by + (walBtnH - dh) / 2;
            ctx.drawImage(btn.img, imgX, imgY, dw, dh);
            // Text to the right of the logo
            ctx.font      = '600 12px Inter, system-ui, sans-serif';
            ctx.fillStyle = '#ffffff';
            ctx.textAlign = 'left';
            ctx.fillText(btn.label, imgX + dw + 8, by + walBtnH / 2 + 4);
        } else {
            ctx.font      = '600 12px Inter, system-ui, sans-serif';
            ctx.fillStyle = '#ffffff';
            ctx.textAlign = 'center';
            ctx.fillText(btn.label, bx + walBtnW / 2, by + walBtnH / 2 + 4);
        }
    });
    ctx.textAlign = 'left';

    // ── 7. DISCLAIMER ────────────────────────────────────────────────────────
    const DISC_Y = FOOT_Y + actualFootH;
    ctx.fillStyle = '#f8fafc';
    ctx.fillRect(0, DISC_Y, CANVAS_W, actualDiscH);

    ctx.strokeStyle = '#e2e8f0';
    ctx.lineWidth   = 1;
    ctx.beginPath();
    ctx.moveTo(0, DISC_Y);
    ctx.lineTo(CANVAS_W, DISC_Y);
    ctx.stroke();

    const DISC_PAD_H = 48;
    const DISC_MAX_W = CANVAS_W - DISC_PAD_H * 2;

    // Trademark note — wraps to full width
    ctx.font      = '14px Inter, system-ui, sans-serif';
    ctx.fillStyle = '#94a3b8';
    const tmLines = wrapText(ctx, 'Pharmacy names, logos, brands, and other trademarks are the property of their respective owners.', DISC_MAX_W);
    tmLines.forEach((line, i) => ctx.fillText(line, DISC_PAD_H, DISC_Y + DISC_PAD_V + TM_LINE_H + i * TM_LINE_H));

    // ── Paragraph 2: mixed bold / normal disclaimer ──────────────────────────
    const progName = legalDisplayName(design);
    const discSegs = [
        { bold: true,  text: `The ${progName} prescription discount program is NOT insurance` },
        { bold: false, text: `. It is also not a Medicare prescription drug plan. The range of prescription discounts provided under this discount program will vary depending on the prescription and pharmacy where the prescription is purchased and can be up to $95 off the retail price. ` },
        { bold: true,  text: `Purchases using the ${progName} program do not count towards insurance or Medicare deductibles or out-of-pocket maximums.` },
        { bold: false, text: ` You are fully responsible for paying your prescriptions at the pharmacy at the time of service, but you will be entitled to receive a discount from the pharmacy in accordance with the specific pre-negotiated discounted rate schedule. Glic Health LLC is the Administrator of the ${progName} prescription discount program with its office located at One Marina Park Drive, Suite 1410, Boston MA 02210. Toll Free at (844) 944-4542 and email at support@glichealth.com. For additional information, including an up-to-date list of pharmacies and covered drugs, or assistance with any problems related to this prescription drug discount plan, please contact our member support line toll free at (800) 999-3053 or visit ${progName}.com. By using the ${progName} prescription discount program or app, you agree to the ${progName} Terms and Conditions found at https://demo.glicrx.com/terms.` },
    ];

    const discStartY = DISC_Y + DISC_PAD_V + tmLines.length * TM_LINE_H + DISC_INNER_GAP;
    const nDiscLines = drawRichText(ctx, discSegs, DISC_PAD_H, discStartY, DISC_MAX_W, DISC_LINE_H, 9.5, '#475569');

    // ── Copyright bar — equal top/bottom padding inside the bar ────────────────
    const copyDividerY = discStartY + nDiscLines * DISC_LINE_H + DISC_PAD_V;
    ctx.strokeStyle = '#e2e8f0';
    ctx.lineWidth   = 1;
    ctx.beginPath();
    ctx.moveTo(0, copyDividerY);
    ctx.lineTo(CANVAS_W, copyDividerY);
    ctx.stroke();

    ctx.font      = '14px Inter, system-ui, sans-serif';
    ctx.fillStyle = '#64748b';
    ctx.textAlign = 'center';
    ctx.fillText(
        `Terms of Use  |  Privacy  |  © 2025 ${progName}. All rights reserved.`,
        CANVAS_W / 2, copyDividerY + COPY_PAD_V + COPY_TEXT_BASE
    );
    ctx.textAlign = 'left';
}

// ── Form sub-components ─────────────────────────────────────────────────────

function FormField({ label, children, error, required }) {
    return (
        <div className="flex flex-col gap-1.5">
            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                {label}{required && <span className="text-destructive ml-0.5">*</span>}
            </Label>
            {children}
            {error && <p className="text-xs text-destructive mt-0.5">{error}</p>}
        </div>
    );
}

function ColorSwatch({ value, onChange, label }) {
    const id = `color-${label.replace(/\s+/g, '-')}`;
    return (
        <div className="flex flex-col gap-1.5">
            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</Label>
            <div className="flex items-center gap-2">
                <div
                    className="h-8 w-8 rounded-md border border-input cursor-pointer shrink-0"
                    style={{ background: value }}
                    onClick={() => document.getElementById(id).click()}
                />
                <Input
                    value={value}
                    onChange={e => onChange(e.target.value)}
                    className="h-8 font-mono text-xs"
                    maxLength={7}
                />
                <input
                    id={id}
                    type="color"
                    value={value}
                    onChange={e => onChange(e.target.value)}
                    className="sr-only"
                />
            </div>
        </div>
    );
}

// ── Validation helpers ───────────────────────────────────────────────────────

const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
const isValidUsPhone = (v) => validatePhoneNumber(v?.trim());
const isValidUrl   = (url) => {
    if (!url) return true;
    try {
        const u = new URL(url.startsWith('http') ? url : `https://${url}`);
        return u.protocol === 'http:' || u.protocol === 'https:';
    } catch { return false; }
};

const VALIDATORS = {
    logoDataUrl    : v => !v ? 'Logo is required' : '',
    programName    : v => !v?.trim() ? 'Brand name is required' : '',
    legalName      : v => !v?.trim() ? 'Company legal name is required' : '',
    mailingAddress : v => !v?.trim() ? 'Mailing address is required' : '',
    contactEmail   : v => !v?.trim() ? 'Contact email is required' : !isValidEmail(v) ? 'Enter a valid email address' : '',
    contactPhone   : v => !v?.trim() ? 'Contact phone is required' : !isValidUsPhone(v) ? 'Enter a valid US phone number (e.g. (800) 555-0100)' : '',
    bin            : v => !v?.trim() ? 'BIN is required' : !/^\d{6}$/.test(v.trim()) ? 'BIN must be exactly 6 digits' : '',
    pcn            : v => !v?.trim() ? 'PCN is required' : '',
    group          : v => !v?.trim() ? 'Group number is required' : '',
    memberId       : v => !v?.trim() ? 'Member ID is required' : '',
    website        : v => v?.trim() && !DOMAIN_REGEX.test(v.trim()) ? 'Enter a valid domain (e.g. example.com or sub.example.com)' : '',
    phone          : v => !v?.trim() ? 'Support phone is required' : !isValidUsPhone(v) ? 'Enter a valid US phone number (e.g. 1-800-555-0100)' : '',
    socialFacebook : v => v && !isValidUrl(v) ? 'Enter a valid URL' : '',
    socialInstagram: v => v && !isValidUrl(v) ? 'Enter a valid URL' : '',
    socialTwitter  : v => v && !isValidUrl(v) ? 'Enter a valid URL' : '',
    socialLinkedin : v => v && !isValidUrl(v) ? 'Enter a valid URL' : '',
};

// Fields required per step (used to gate Next navigation)
const STEP_REQUIRED = [
    ['logoDataUrl', 'legalName', 'mailingAddress', 'contactEmail', 'contactPhone'],  // step 0
    [],                                                                // step 1 colors
    ['bin', 'pcn', 'group', 'memberId', 'phone'],                     // step 2 card
    [],                                                                // step 3 social (optional)
];

// ── Page component ──────────────────────────────────────────────────────────

function CanvasDesigner() {
    const history = useHistory();
    const canvasRef      = useRef(null);
    const logoImgRef     = useRef(null);
    const stepImgsRef    = useRef([null, null, null]);
    const introBgRef     = useRef(null);
    const pharmaImgsRef  = useRef(Array(9).fill(null));
    const socialIconsRef = useRef({});
    const walletImgsRef  = useRef([null, null]);
    const searchIconRef  = useRef(null);
    const cardFrontImgRef = useRef(null);
    const cardBackImgRef  = useRef(null);
    // Keep a ref to the latest design so image onLoad callbacks can read it
    // without needing state — avoids any React re-render on image load.
    const designRef      = useRef(DEFAULT_DESIGN);
    const [design, setDesign]       = useState(DEFAULT_DESIGN);
    const [logoError, setLogoError] = useState('');
    const [currentStep, setCurrentStep] = useState(0);
    const [errors,  setErrors]  = useState({});
    const [touched, setTouched] = useState({});
    const [websiteValidating, setWebsiteValidating] = useState(false);
    const [websiteValidated, setWebsiteValidated] = useState(false);

    const userdata = useMemo(() => {
        try { return JSON.parse(localStorage.getItem('user')); } catch { return null; }
    }, []);

    const redrawCanvas = useCallback((currentDesign) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        drawCanvas(
            canvas, currentDesign, logoImgRef.current,
            stepImgsRef.current, introBgRef.current,
            pharmaImgsRef.current, socialIconsRef.current, walletImgsRef.current,
            searchIconRef.current, cardFrontImgRef.current, cardBackImgRef.current
        );
    }, []);

    const update = useCallback((key, value) => {
        setDesign(prev => ({ ...prev, [key]: value }));
    }, []);

    // Keep designRef in sync — no re-render triggered
    useEffect(() => { designRef.current = design; }, [design]);

    // Pre-load all static assets once on mount.
    // When done, redraw directly via ref — NO state update, NO re-render.
    useEffect(() => {
        const stepSrcs = [behalfIcon1, behalfIcon2, behalfIcon3];
        const socialIconSrcs = SOCIAL_ICON_FIELDS.map(field => SOCIAL_ICON_BY_FIELD[field]);
        const allSrcs  = [...stepSrcs, introBg, ...PHARMA_LOGO_SRCS, ...socialIconSrcs, ...WALLET_SRCS, iconSearch];
        let loaded = 0;
        const onLoad = () => {
            loaded += 1;
            if (loaded === allSrcs.length) {
                // Direct draw — never touches React state, so no re-render
                redrawCanvas(designRef.current);
            }
        };

        stepSrcs.forEach((src, i) => {
            const img = new Image(); img.onload = onLoad; img.onerror = onLoad; img.src = src;
            stepImgsRef.current[i] = img;
        });

        const bgImg = new Image(); bgImg.onload = onLoad; bgImg.onerror = onLoad; bgImg.src = introBg;
        introBgRef.current = bgImg;

        PHARMA_LOGO_SRCS.forEach((src, i) => {
            const img = new Image(); img.onload = onLoad; img.onerror = onLoad; img.src = src;
            pharmaImgsRef.current[i] = img;
        });

        SOCIAL_ICON_FIELDS.forEach(field => {
            const img = new Image(); img.onload = onLoad; img.onerror = onLoad;
            img.src = SOCIAL_ICON_BY_FIELD[field];
            socialIconsRef.current[field] = img;
        });

        WALLET_SRCS.forEach((src, i) => {
            const img = new Image(); img.onload = onLoad; img.onerror = onLoad; img.src = src;
            walletImgsRef.current[i] = img;
        });

        const srchImg = new Image(); srchImg.onload = onLoad; srchImg.onerror = onLoad; srchImg.src = iconSearch;
        searchIconRef.current = srchImg;
    }, [redrawCanvas]);

    // Redraw whenever design changes (form input)
    useEffect(() => {
        redrawCanvas(design);
    }, [design, redrawCanvas]);

    const userType = userService.Get_User_Type();
    if (userService.Check_LoggedIn_Status() === 'Login') return <Redirect to="/login" />;
    if (['admin', 'distributor', 'subadmin'].includes(userType?.toLowerCase())) return <Redirect to='/pageNotFound' />;
    if (userdata?.wholesaler_Id === 61) return <Redirect to='/pageNotFound' />;

    const handleLogoUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setLogoError('');
        const isAllowedType = LOGO_MIME_TYPES.includes(file.type) || LOGO_EXT_PATTERN.test(file.name);
        if (!isAllowedType) {
            setLogoError('Logo must be a PNG or SVG file.');
            e.target.value = '';
            return;
        }
        const reader = new FileReader();
        reader.onload = (ev) => {
            const img = new Image();
            img.onload = () => {
                if (img.width < LOGO_MIN_PX) {
                    setLogoError(`Logo too small — minimum ${LOGO_MIN_PX}px wide (yours: ${img.width}px).`);
                    return;
                }
                if (img.width > LOGO_MAX_PX) {
                    setLogoError(`Logo too large — maximum ${LOGO_MAX_PX}px wide (yours: ${img.width}px).`);
                    return;
                }
                logoImgRef.current = img;
                setLogoError('');
                touch('logoDataUrl');
                validateField('logoDataUrl', ev.target.result);
                update('logoDataUrl', ev.target.result);
            };
            img.src = ev.target.result;
        };
        reader.readAsDataURL(file);
        // reset input so the same file can be re-selected after removal
        e.target.value = '';
    };

    const handleRemoveLogo = () => {
        logoImgRef.current = null;
        setLogoError('');
        touch('logoDataUrl');
        update('logoDataUrl', null);
        validateField('logoDataUrl', null);
    };

    const handlePdfUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const url = URL.createObjectURL(file);
        setDesign(prev => ({ ...prev, pdfFileName: file.name, pdfFileUrl: url }));
        e.target.value = '';
    };

    const handleRemovePdf = () => {
        if (design.pdfFileUrl) URL.revokeObjectURL(design.pdfFileUrl);
        setDesign(prev => ({ ...prev, pdfFileName: null, pdfFileUrl: null }));
    };

    const handleCardImgUpload = (field) => (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => {
            const img = new Image();
            img.onload = () => {
                if (field === 'cardFrontDataUrl') cardFrontImgRef.current = img;
                else cardBackImgRef.current = img;
                update(field, ev.target.result);
            };
            img.src = ev.target.result;
        };
        reader.readAsDataURL(file);
        e.target.value = '';
    };

    const handleRemoveCardImg = (field) => {
        if (field === 'cardFrontDataUrl') cardFrontImgRef.current = null;
        else cardBackImgRef.current = null;
        update(field, null);
    };

    const touch = (field) => setTouched(prev => ({ ...prev, [field]: true }));

    const validateField = (field, value) => {
        const err = VALIDATORS[field] ? VALIDATORS[field](value) : '';
        setErrors(prev => ({ ...prev, [field]: err }));
        return err;
    };

    const updateAndValidate = (field, value) => {
        update(field, value);
        if (field === 'website') setWebsiteValidated(false);
        if (touched[field] && VALIDATORS[field]) validateField(field, value);
    };

    const validateWebsiteDomain = async (value) => {
        touch('website');
        const trimmed = value?.trim() || '';
        const formatErr = VALIDATORS.website(trimmed);
        if (formatErr || !trimmed) {
            setWebsiteValidated(false);
            validateField('website', trimmed);
            return;
        }
        if (!userdata?.wholesaler_Id || !userdata?.email) {
            setWebsiteValidated(false);
            setErrors(prev => ({ ...prev, website: 'Unable to validate domain. Please sign in again.' }));
            return;
        }

        setWebsiteValidating(true);
        setWebsiteValidated(false);
        try {
            const apidata = await userService.ProcessWhiteListIP(
                userdata.wholesaler_Id.toString(),
                userdata.email,
                trimmed,
                true
            );
            if (apidata[0]?.code === '1') {
                setWebsiteValidated(true);
                setErrors(prev => ({ ...prev, website: '' }));
            } else {
                setWebsiteValidated(false);
                setErrors(prev => ({
                    ...prev,
                    website: apidata[0]?.message || 'This domain/subdomain is not valid.',
                }));
            }
        } catch {
            setWebsiteValidated(false);
            setErrors(prev => ({ ...prev, website: 'Unable to validate domain. Please try again.' }));
        } finally {
            setWebsiteValidating(false);
        }
    };

    const updateSocial = (field, value) => {
        update(field, value);
        touch(field);
        if (VALIDATORS[field]) validateField(field, value);
    };

    const validateStepFields = (fields) => {
        const newTouched = {};
        const newErrors  = {};
        fields.forEach(f => {
            newTouched[f] = true;
            const err = VALIDATORS[f]?.(design[f]) || '';
            if (err) newErrors[f] = err;
        });
        setTouched(prev => ({ ...prev, ...newTouched }));
        if (Object.keys(newErrors).length) {
            setErrors(prev => ({ ...prev, ...newErrors }));
            return false;
        }
        return true;
    };

    const handleNext = () => {
        const required = STEP_REQUIRED[currentStep] || [];
        if (!validateStepFields(required)) return;
        setCurrentStep(s => Math.min(STEP_LABELS.length - 1, s + 1));
    };

    const handleStepClick = (targetStep) => {
        if (targetStep === currentStep) return;
        if (targetStep < currentStep) {
            setCurrentStep(targetStep);
            return;
        }
        for (let s = currentStep; s < targetStep; s++) {
            if (!validateStepFields(STEP_REQUIRED[s] || [])) return;
        }
        setCurrentStep(targetStep);
    };

    const handleSubmit = () => {
        const allRequired = STEP_REQUIRED.flat();
        const socialFields = ['socialFacebook', 'socialInstagram', 'socialTwitter', 'socialLinkedin'];
        const fieldsToValidate = [
            ...allRequired,
            ...socialFields.filter(f => design[f]?.trim()),
        ];
        if (!validateStepFields(fieldsToValidate)) return;
        // TODO: call save API when backend endpoint is ready
        console.log('Submit whitelabel design:', design);
    };

    const STEP_LABELS      = ['Info & Brand', 'Colors', 'Card & Docs', 'Social'];
    const STEP_FULL_LABELS = ['Business & Brand', 'Colors & Typography', 'Card & Documents', 'Social Media Links'];

    const handleDownload = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const link = document.createElement('a');
        link.download = `${brandDisplayName(design).replace(/[^\w.-]+/g, '-')}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
    };

    const handleReset = () => {
        logoImgRef.current = null;
        cardFrontImgRef.current = null;
        cardBackImgRef.current = null;
        setLogoError('');
        setErrors({});
        setTouched({});
        setWebsiteValidating(false);
        setWebsiteValidated(false);
        if (design.pdfFileUrl) URL.revokeObjectURL(design.pdfFileUrl);
        setCurrentStep(0);
        setDesign(DEFAULT_DESIGN);
    };

    return (
        <PagesWrapper>
            <div className="space-y-6">
                <Button
                    variant="default"
                    size="sm"
                    className="gap-1"
                    onClick={() => history.push('/wholesaler/tools')}
                >
                    <ChevronLeft className="h-4 w-4" />
                    Back to Tools
                </Button>

                {/* Header */}
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <h1 className="text-2xl font-semibold text-foreground flex items-center gap-2">
                        <Paintbrush className="h-6 w-6 text-primary" />
                        Whitelabel Setup
                        <BetaBadge />
                    </h1>
                    <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={handleReset}>
                            <RotateCcw className="h-4 w-4" />
                            Reset
                        </Button>
                        <Button size="sm" onClick={handleDownload}>
                            <Download className="h-4 w-4" />
                            Download PNG
                        </Button>
                    </div>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-[360px_1fr] gap-6 items-start">

                    {/* ── Form Panel (Stepper) ── */}
                    <div className="space-y-3">

                        {/* Step indicators */}
                        <div className="flex items-start w-full">
                            {STEP_LABELS.map((label, i) => (
                                <React.Fragment key={i}>
                                    {i > 0 && (
                                        <div
                                            className="h-px flex-1 min-w-2 mt-[14px]"
                                            style={{ backgroundColor: i - 1 < currentStep ? 'var(--primary)' : 'var(--border)' }}
                                        />
                                    )}
                                    <button
                                        className="flex flex-col items-center gap-1 shrink-0 w-16"
                                        onClick={() => handleStepClick(i)}
                                    >
                                        <div
                                            className="h-7 w-7 rounded-full flex items-center justify-center text-xs font-semibold border-2 transition-colors shrink-0"
                                            style={{
                                                backgroundColor: i <= currentStep ? 'var(--primary)' : 'var(--muted)',
                                                borderColor:     i <= currentStep ? 'var(--primary)' : 'var(--border)',
                                                color:           i <= currentStep ? 'var(--primary-foreground)' : 'var(--muted-foreground)',
                                            }}
                                        >
                                            {i < currentStep ? <Check className="h-3 w-3" /> : i + 1}
                                        </div>
                                        <span
                                            className="text-[9px] font-medium text-center leading-tight whitespace-nowrap"
                                            style={{ color: i === currentStep ? 'var(--primary)' : 'var(--muted-foreground)' }}
                                        >
                                            {label}
                                        </span>
                                    </button>
                                </React.Fragment>
                            ))}
                        </div>

                        {/* Step content card */}
                        <Card>
                            <CardHeader className="py-2 px-4 sm:py-2 sm:px-4">
                                <CardTitle className="text-sm font-semibold">{STEP_FULL_LABELS[currentStep]}</CardTitle>
                            </CardHeader>
                            <CardContent className="px-4 pb-4 pt-0 space-y-4 max-h-[420px] overflow-y-auto [&_input:not([type=file]):not([type=color])]:placeholder:text-muted-foreground/60 [&_textarea]:placeholder:text-muted-foreground/60">

                                {/* Step 0 — Business & Brand */}
                                {currentStep === 0 && (
                                    <>
                                        {!design.logoDataUrl && (
                                        <FormField label="Brand Name">
                                            <Input
                                                placeholder="e.g. DemoRx"
                                                value={design.programName}
                                                onChange={e => update('programName', e.target.value)}
                                            />
                                        </FormField>
                                        )}
                                        <FormField label={`Logo (PNG or SVG · min ${LOGO_MIN_PX}px wide)`} required error={touched.logoDataUrl && (logoError || errors.logoDataUrl)}>
                                            {design.logoDataUrl ? (
                                                <div className="flex items-start gap-3 flex-wrap">
                                                    <img
                                                        src={design.logoDataUrl}
                                                        alt={design.programName || 'logo'}
                                                        className="min-w-[200px] w-auto max-w-full max-h-24 rounded-md object-contain border border-input bg-muted"
                                                    />
                                                    <div className="flex flex-col gap-0.5">
                                                        <span className="text-xs text-green-600 font-medium">Uploaded</span>
                                                        <Button variant="outline" size="sm" onClick={handleRemoveLogo} className="gap-1 h-7 text-xs">
                                                            <X className="h-3 w-3" /> Remove
                                                        </Button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <label
                                                    className="flex items-center gap-2 h-10 px-3 rounded-md border border-dashed border-input bg-muted/30 cursor-pointer hover:bg-muted/60 transition-colors text-sm text-muted-foreground"
                                                    onBlur={() => { touch('logoDataUrl'); validateField('logoDataUrl', design.logoDataUrl); }}
                                                    tabIndex={0}
                                                >
                                                    <ImagePlus className="h-4 w-4 shrink-0" />
                                                    Upload logo
                                                    <input type="file" accept={LOGO_ACCEPT} className="hidden" onChange={handleLogoUpload} />
                                                </label>
                                            )}
                                        </FormField>
                                        <FormField label="Company Legal Name" required error={touched.legalName && errors.legalName}>
                                            <Input
                                                placeholder="e.g. Acme Pharmacy Inc."
                                                value={design.legalName}
                                                onChange={e => updateAndValidate('legalName', e.target.value)}
                                                onBlur={() => { touch('legalName'); validateField('legalName', design.legalName); }}
                                            />
                                        </FormField>
                                        <FormField label="Mailing Address" required error={touched.mailingAddress && errors.mailingAddress}>
                                            <Textarea
                                                placeholder={"123 Main St, Suite 100\nBoston, MA 02210"}
                                                value={design.mailingAddress}
                                                onChange={e => updateAndValidate('mailingAddress', e.target.value)}
                                                onBlur={() => { touch('mailingAddress'); validateField('mailingAddress', design.mailingAddress); }}
                                                className="min-h-[72px] resize-none text-sm"
                                            />
                                        </FormField>
                                        <FormField label="Contact Email" required error={touched.contactEmail && errors.contactEmail}>
                                            <Input
                                                type="email"
                                                placeholder="support@yourcompany.com"
                                                value={design.contactEmail}
                                                onChange={e => updateAndValidate('contactEmail', e.target.value)}
                                                onBlur={() => { touch('contactEmail'); validateField('contactEmail', design.contactEmail); }}
                                            />
                                        </FormField>
                                        <FormField label="Contact Phone" required error={touched.contactPhone && errors.contactPhone}>
                                            <Input
                                                placeholder="1-800-000-0000"
                                                value={design.contactPhone}
                                                onChange={e => updateAndValidate('contactPhone', e.target.value)}
                                                onBlur={() => { touch('contactPhone'); validateField('contactPhone', design.contactPhone); }}
                                                maxLength={PHONE_MAX_LENGTH}
                                            />
                                        </FormField>
                                    </>
                                )}

                                {/* Step 1 — Colors & Typography */}
                                {currentStep === 1 && (
                                    <>
                                        <ColorSwatch label="Heading Color"           value={design.headingColor}   onChange={v => update('headingColor', v)} />
                                        <ColorSwatch label="Paragraph / Body Color"  value={design.paragraphColor} onChange={v => update('paragraphColor', v)} />
                                        <ColorSwatch label="Link / Active Color"     value={design.linkColor}      onChange={v => update('linkColor', v)} />
                                        <ColorSwatch label="Button Background Color" value={design.btnBgColor}     onChange={v => update('btnBgColor', v)} />
                                        <ColorSwatch label="Button Text Color"       value={design.btnFgColor}     onChange={v => update('btnFgColor', v)} />
                                    </>
                                )}

                                {/* Step 2 — Card & Documents */}
                                {currentStep === 2 && (
                                    <>
                                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide -mb-1">Card Fields</p>
                                        <div className="grid grid-cols-2 gap-3">
                                            <FormField label="BIN" required error={touched.bin && errors.bin}>
                                                <Input placeholder="023518" value={design.bin}
                                                    onChange={e => updateAndValidate('bin', e.target.value)}
                                                    onBlur={() => { touch('bin'); validateField('bin', design.bin); }}
                                                    maxLength={10} />
                                            </FormField>
                                            <FormField label="PCN" required error={touched.pcn && errors.pcn}>
                                                <Input placeholder="ARK" value={design.pcn}
                                                    onChange={e => updateAndValidate('pcn', e.target.value.toUpperCase())}
                                                    onBlur={() => { touch('pcn'); validateField('pcn', design.pcn); }}
                                                    maxLength={20} />
                                            </FormField>
                                            <FormField label="Group Number" required error={touched.group && errors.group}>
                                                <Input placeholder="GHRX525" value={design.group}
                                                    onChange={e => updateAndValidate('group', e.target.value.toUpperCase())}
                                                    onBlur={() => { touch('group'); validateField('group', design.group); }}
                                                    maxLength={20} />
                                            </FormField>
                                            <FormField label="Member ID" required error={touched.memberId && errors.memberId}>
                                                <Input placeholder="100-4542" value={design.memberId}
                                                    onChange={e => updateAndValidate('memberId', e.target.value)}
                                                    onBlur={() => { touch('memberId'); validateField('memberId', design.memberId); }}
                                                    maxLength={20} />
                                            </FormField>
                                        </div>

                                        <FormField label="Website" error={touched.website && errors.website}>
                                            <div className="relative">
                                                <Input
                                                    placeholder="example.com or sub.example.com"
                                                    value={design.website}
                                                    onChange={e => updateAndValidate('website', e.target.value)}
                                                    onBlur={() => validateWebsiteDomain(design.website)}
                                                    className={websiteValidating ? 'pr-9' : undefined}
                                                />
                                                {websiteValidating && (
                                                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
                                                )}
                                            </div>
                                            {websiteValidated && (
                                                <div className="mt-2 rounded-md border border-green-200 bg-green-50 dark:bg-green-950/20 p-3 space-y-2">
                                                    <p className="text-xs font-semibold text-green-800 dark:text-green-300 flex items-center gap-1">
                                                        <Check className="h-3.5 w-3.5" />
                                                        Domain verified — add these DNS records at your registrar:
                                                    </p>
                                                    <div className="rounded border border-border bg-background/80 overflow-hidden text-xs">
                                                        <div className="grid grid-cols-[5rem_4rem_1fr] gap-2 px-3 py-1.5 bg-muted/50 font-medium text-muted-foreground">
                                                            <span>Type</span>
                                                            <span>Host</span>
                                                            <span>Value</span>
                                                        </div>
                                                        {[WHITELABEL_DNS_RECORDS.cname, WHITELABEL_DNS_RECORDS.aRecord].map(record => (
                                                            <div key={record.type} className="grid grid-cols-[5rem_4rem_1fr] gap-2 px-3 py-1.5 border-t border-border font-mono">
                                                                <span>{record.type}</span>
                                                                <span>{record.host}</span>
                                                                <span className="break-all">{record.value}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </FormField>

                                        <FormField label="Support Phone" required error={touched.phone && errors.phone}>
                                            <Input placeholder="1-800-000-0000" value={design.phone}
                                                onChange={e => updateAndValidate('phone', e.target.value)}
                                                onBlur={() => { touch('phone'); validateField('phone', design.phone); }}
                                                maxLength={PHONE_MAX_LENGTH} />
                                        </FormField>

                                        <div className="border-t border-border pt-3 mt-1">
                                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Documents</p>
                                        </div>

                                        {/* PDF */}
                                        <FormField label="PDF Card (print version)">
                                            {design.pdfFileName ? (
                                                <div className="flex items-center gap-3 p-2.5 rounded-md border border-input bg-muted/30">
                                                    <FileText className="h-8 w-8 text-red-500 shrink-0" />
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-xs font-medium text-foreground truncate">{design.pdfFileName}</p>
                                                        <p className="text-xs text-green-600">Uploaded</p>
                                                    </div>
                                                    <div className="flex gap-1.5 shrink-0">
                                                        <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => window.open(design.pdfFileUrl, '_blank')}>View</Button>
                                                        <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={handleRemovePdf}><X className="h-3 w-3" /></Button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <label className="flex items-center gap-2 h-10 px-3 rounded-md border border-dashed border-input bg-muted/30 cursor-pointer hover:bg-muted/60 transition-colors text-sm text-muted-foreground">
                                                    <FileText className="h-4 w-4 shrink-0" />
                                                    Upload PDF version of your card
                                                    <input type="file" accept="application/pdf" className="hidden" onChange={handlePdfUpload} />
                                                </label>
                                            )}
                                        </FormField>

                                        {/* Card Front Image */}
                                        <FormField label="Card Front Image">
                                            {design.cardFrontDataUrl ? (
                                                <div className="flex items-center gap-3">
                                                    <div
                                                        className="relative shrink-0 rounded-md border border-input bg-muted overflow-hidden"
                                                        style={{ width: 220, height: Math.round(220 * MINI_CARD_H / MINI_CARD_W) }}
                                                    >
                                                        <img src={design.cardFrontDataUrl} alt="card front" className="absolute inset-0 w-full h-full object-cover object-top" />
                                                    </div>
                                                    <div className="flex flex-col gap-0.5">
                                                        <span className="text-xs text-green-600 font-medium">Uploaded</span>
                                                        <Button variant="outline" size="sm" onClick={() => handleRemoveCardImg('cardFrontDataUrl')} className="gap-1 h-7 text-xs">
                                                            <X className="h-3 w-3" /> Remove
                                                        </Button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <label className="flex items-center gap-2 h-10 px-3 rounded-md border border-dashed border-input bg-muted/30 cursor-pointer hover:bg-muted/60 transition-colors text-sm text-muted-foreground">
                                                    <ImagePlus className="h-4 w-4 shrink-0" />
                                                    Upload card front image
                                                    <input type="file" accept="image/*" className="hidden" onChange={handleCardImgUpload('cardFrontDataUrl')} />
                                                </label>
                                            )}
                                        </FormField>

                                        {/* Card Back Image */}
                                        <FormField label="Card Back Image">
                                            {design.cardBackDataUrl ? (
                                                <div className="flex items-center gap-3">
                                                    <div
                                                        className="relative shrink-0 rounded-md border border-input bg-muted overflow-hidden"
                                                        style={{ width: 220, height: Math.round(220 * MINI_CARD_H / MINI_CARD_W) }}
                                                    >
                                                        <img src={design.cardBackDataUrl} alt="card back" className="absolute inset-0 w-full h-full object-cover object-top" />
                                                    </div>
                                                    <div className="flex flex-col gap-0.5">
                                                        <span className="text-xs text-green-600 font-medium">Uploaded</span>
                                                        <Button variant="outline" size="sm" onClick={() => handleRemoveCardImg('cardBackDataUrl')} className="gap-1 h-7 text-xs">
                                                            <X className="h-3 w-3" /> Remove
                                                        </Button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <label className="flex items-center gap-2 h-10 px-3 rounded-md border border-dashed border-input bg-muted/30 cursor-pointer hover:bg-muted/60 transition-colors text-sm text-muted-foreground">
                                                    <ImagePlus className="h-4 w-4 shrink-0" />
                                                    Upload card back image
                                                    <input type="file" accept="image/*" className="hidden" onChange={handleCardImgUpload('cardBackDataUrl')} />
                                                </label>
                                            )}
                                        </FormField>
                                    </>
                                )}

                                {/* Step 3 — Social Media Links */}
                                {currentStep === 3 && (
                                    <>
                                        <p className="text-xs text-muted-foreground -mt-1">Leave blank to hide the &ldquo;Follow&rdquo; section from the canvas.</p>
                                        <FormField label="Facebook" error={touched.socialFacebook && errors.socialFacebook}>
                                            <Input placeholder="https://facebook.com/yourpage" value={design.socialFacebook}
                                                onChange={e => updateSocial('socialFacebook', e.target.value)} />
                                        </FormField>
                                        <FormField label="Instagram" error={touched.socialInstagram && errors.socialInstagram}>
                                            <Input placeholder="https://instagram.com/yourpage" value={design.socialInstagram}
                                                onChange={e => updateSocial('socialInstagram', e.target.value)} />
                                        </FormField>
                                        <FormField label="Twitter / X" error={touched.socialTwitter && errors.socialTwitter}>
                                            <Input placeholder="https://x.com/yourhandle" value={design.socialTwitter}
                                                onChange={e => updateSocial('socialTwitter', e.target.value)} />
                                        </FormField>
                                        <FormField label="LinkedIn" error={touched.socialLinkedin && errors.socialLinkedin}>
                                            <Input placeholder="https://linkedin.com/company/yourco" value={design.socialLinkedin}
                                                onChange={e => updateSocial('socialLinkedin', e.target.value)} />
                                        </FormField>
                                    </>
                                )}

                            </CardContent>
                        </Card>

                        {/* Step navigation */}
                        <div className="flex justify-between items-center">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCurrentStep(s => Math.max(0, s - 1))}
                                disabled={currentStep === 0}
                                className="gap-1"
                            >
                                <ChevronLeft className="h-4 w-4" /> Previous
                            </Button>
                            <span className="text-xs text-muted-foreground">
                                {currentStep + 1} / {STEP_LABELS.length}
                            </span>
                            <Button
                                size="sm"
                                onClick={currentStep === STEP_LABELS.length - 1 ? handleSubmit : handleNext}
                                className="gap-1"
                            >
                                {currentStep === STEP_LABELS.length - 1 ? (
                                    'Submit'
                                ) : (
                                    <>Next <ChevronRight className="h-4 w-4" /></>
                                )}
                            </Button>
                        </div>

                    </div>

                    {/* ── Canvas Preview Panel ── */}
                    <div className="flex flex-col gap-3">
                        <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Live Preview</p>
                        <div className="w-full overflow-y-auto rounded-xl border border-border bg-muted/30 p-4 max-h-[80vh]">
                            <canvas
                                ref={canvasRef}
                                width={CANVAS_W}
                                className="shadow-xl w-full"
                                style={{ display: 'block' }}
                            />
                        </div>
                        <p className="text-xs text-muted-foreground text-center">
                            {CANVAS_W}px wide — Click <strong>Download PNG</strong> to save the full page
                        </p>
                    </div>
                </div>
            </div>
        </PagesWrapper>
    );
}

export default CanvasDesigner;
